import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Link,
  TextField,
  Typography,
} from '@mui/material'
import { grey } from '@mui/material/colors'
import { type GridColDef, type GridRenderCellParams } from '@mui/x-data-grid'
import { useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'

export interface RedrawnPatrons {
  id: number
  drawingNumber: number
  patronId: number
  name: string
  phoneNumber: string
  email: string
  tierLevel: string
  gender: string
  birthday: string
  totalEntriesByWinner: number
  dateRedrawn: string
  reasonForRedraw: string
  affidavitCompletedAt?: string
  notes?: string
}

// Component for displaying notes in a dialog
const NotesDialogCell = ({ value }: { value: string }) => {
  const [isOpen, setIsOpen] = useState(false)
  const handleOpen = () => setIsOpen(true)
  const handleClose = () => setIsOpen(false)

  return (
    <>
      <Typography
        onClick={handleOpen}
        sx={{
          cursor: 'pointer',
          color: '#1A4F64',
          textDecoration: 'underline',
          '&:hover': {
            color: '#0d3a4f',
          },
        }}
      >
        {value ? 'View Notes' : 'No Notes'}
      </Typography>
      <Dialog open={isOpen} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Redrawn Notes</DialogTitle>
        <Box sx={{ px: 2, pb: 2 }}>
          <DialogContent>
            <TextField
              fullWidth
              multiline
              rows={10}
              value={value}
              variant="outlined"
              InputProps={{
                readOnly: true,
              }}
            />
          </DialogContent>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: 2,
            p: 2,
            justifyContent: 'flex-end',
            borderTop: 1,
            borderTopColor: grey[300],
          }}
        >
          <Button onClick={handleClose} variant="outlined">
            Cancel
          </Button>
        </Box>
      </Dialog>
    </>
  )
}

export const getRedrawnPatronsColumns = (): GridColDef[] => [
  {
    field: 'drawingNumber',
    headerName: 'Drawing Number',
    width: 165,
    renderCell: (params: GridRenderCellParams) => {
      return <Typography>{params.value}</Typography>
    },
  },
  {
    field: 'name',
    headerName: 'Name',
    width: 230,
    renderCell: (params) => (
      <Link
        to={`/PatronAccounts/${String(params.row.patronId)}/Overview`}
        component={RouterLink}
        underline="hover"
        color="text.primary"
        sx={{
          '&:hover': {
            color: '#1A4F64',
          },
        }}
      >
        <Typography fontWeight={'regular'}>{params.value}</Typography>
      </Link>
    ),
  },
  {
    field: 'phoneNumber',
    headerName: 'Phone Number',
    width: 160,
    renderCell: (params: GridRenderCellParams) => {
      return <Typography>{params.value}</Typography>
    },
  },
  {
    field: 'email',
    headerName: 'Email',
    width: 320,
    renderCell: (params: GridRenderCellParams) => {
      return <Typography>{params.value}</Typography>
    },
  },
  {
    field: 'tierLevel',
    headerName: 'Tier Level',
    width: 100,
    renderCell: (params: GridRenderCellParams) => {
      return <Typography>{params.value}</Typography>
    },
  },
  {
    field: 'gender',
    headerName: 'Gender',
    width: 190,
    renderCell: (params: GridRenderCellParams) => {
      return <Typography>{params.value}</Typography>
    },
  },
  {
    field: 'birthday',
    headerName: 'Birthday',
    width: 100,
    renderCell: (params: GridRenderCellParams) => {
      return <Typography>{params.value}</Typography>
    },
  },
  {
    field: 'totalEntriesByWinner',
    headerName: 'Total Entries by Winner',
    width: 220,
    renderCell: (params: GridRenderCellParams) => {
      return <Typography>{params.value}</Typography>
    },
  },

  {
    field: 'dateRedrawn',
    headerName: 'Date Redrawn',
    width: 190,
    renderCell: (params: GridRenderCellParams) => {
      return <Typography>{params.value}</Typography>
    },
  },
  {
    field: 'reasonForRedraw',
    headerName: 'Reason For Redraw',
    minWidth: 1150,
    renderCell: (params: GridRenderCellParams) => {
      return <Typography>{params.value}</Typography>
    },
  },
  {
    field: 'affidavitCompletedAt',
    headerName: 'Affidavit Completed At',
    width: 200,
    renderCell: (params: GridRenderCellParams) => {
      return <Typography>{params.value}</Typography>
    },
  },
  {
    field: 'notes',
    headerName: 'Notes',
    width: 200,
    renderCell: (params: GridRenderCellParams) => {
      return <NotesDialogCell value={params.value} />
    },
  },
]
